import React, { useState } from 'react';
import {
  Box,
  Fab,
  Paper,
  TextField,
  IconButton,
  Typography,
  Zoom,
  Slide,
  Divider,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

const GEMINI_API_KEY = 'AIzaSyAPY2Ww5mvd2hH6EamQ2fMy6jk6ngS7qlM';

// Course data for the chatbot to reference
const courses = [
  {
    name: "Full Stack Development",
    duration: "6 months",
    level: "Intermediate to Advanced",
    highlights: ["MERN Stack", "Cloud Integration", "Real Projects"]
  },
  {
    name: "Data Science",
    duration: "4 months",
    level: "Beginner to Intermediate",
    highlights: ["Python", "Machine Learning", "Data Analysis"]
  },
  {
    name: "Cloud Computing",
    duration: "3 months",
    level: "Intermediate",
    highlights: ["AWS", "Azure", "DevOps"]
  },
  {
    name: "Cybersecurity",
    duration: "5 months",
    level: "Advanced",
    highlights: ["Network Security", "Ethical Hacking", "Security Tools"]
  }
];

function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{
    text: "Hi! I'm your Upskill Global Tech assistant. How can I help you today? You can ask me about:\n• Our courses and recommendations\n• Course duration and curriculum\n• Career guidance\n• Admission process",
    sender: 'bot'
  }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const generateResponse = async (userInput) => {
    const prompt = `You are a helpful assistant for Upskill Global Technologies. Respond in a friendly, conversational way.
    Here's the context about our courses: ${JSON.stringify(courses)}.
    
    If asked about courses, provide specific details about duration, level, and highlights.
    If asked for recommendations, consider the user's background and interests.
    Keep responses concise but informative.
    
    User question: ${userInput}`;

    try {
      const response = await fetch('https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${GEMINI_API_KEY}`,
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: prompt
            }]
          }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 200,
          }
        })
      });

      const data = await response.json();
      return data.candidates[0].content.parts[0].text;
    } catch (error) {
      console.error('Error:', error);
      return 'Sorry, I encountered an error. Please try again.';
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { text: input, sender: 'user' };
    setMessages([...messages, userMessage]);
    setInput('');
    setLoading(true);

    const response = await generateResponse(input);
    setMessages(prev => [...prev, { text: response, sender: 'bot' }]);
    setLoading(false);
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/919740579800', '_blank');
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
      {/* Chat Window */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: 80,
            right: 0,
            width: 300,
            height: 400,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Chat Header */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
            }}
          >
            {/* Main Header */}
            <Box
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                Upskill Support
              </Typography>
              <IconButton size="small" color="inherit" onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            
            {/* Contact Info */}
            <Box
              sx={{
                bgcolor: 'primary.dark',
                px: 1.5,
                py: 0.5,
                display: 'flex',
                gap: 2,
                fontSize: '0.75rem',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ fontSize: '0.9rem' }} />
                <Typography variant="caption">+91 9740579800</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ fontSize: '0.9rem' }} />
                <Typography variant="caption">tansakeducation@gmail.com</Typography>
              </Box>
            </Box>
          </Box>

          {/* Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              bgcolor: '#f5f5f5',
            }}
          >
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 1,
                    bgcolor: message.sender === 'user' ? 'primary.main' : 'white',
                    color: message.sender === 'user' ? 'white' : 'text.primary',
                    borderRadius: message.sender === 'user' ? '12px 12px 0 12px' : '12px 12px 12px 0',
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-line',
                      fontSize: '0.875rem',
                    }}
                  >
                    {message.text}
                  </Typography>
                </Paper>
              </Box>
            ))}
            {loading && (
              <Box sx={{ alignSelf: 'flex-start' }}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 1, 
                    bgcolor: 'white',
                    borderRadius: '12px 12px 12px 0',
                  }}
                >
                  <Typography variant="body2">Typing...</Typography>
                </Paper>
              </Box>
            )}
          </Box>

          {/* Input Area */}
          <Box sx={{ p: 1.5, bgcolor: 'white', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Type your question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSend} color="primary">
                    <SendIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>
        </Paper>
      </Slide>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Zoom in={!isOpen}>
          <Fab
            color="secondary"
            size="medium"
            onClick={handleWhatsApp}
            sx={{
              bgcolor: '#25D366',
              '&:hover': {
                bgcolor: '#128C7E',
              },
            }}
          >
            <WhatsAppIcon />
          </Fab>
        </Zoom>
        <Fab
          color="primary"
          size="medium"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <CloseIcon /> : <ChatIcon />}
        </Fab>
      </Box>
    </Box>
  );
}

export default ChatBot;
